import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "queryClient";
import { Product } from "types";
import { recalculateProductPrice } from "modules/products/api";

interface RecalculateVariables {
  productID: string;
  expansionID: string;
}

/**
 * Neuberechnung einer einzelnen Karte.
 *
 * Bewusst OHNE die invalidateQueries-Aufrufe aus useProductUpdate: die lösen pro
 * Produkt-Mutation eine komplette, geblätterte Neuladung der Edition aus (bis zu
 * 1000 Zeilen pro Seite) - für eine Karte absurd, und die Tabelle würde flackern.
 *
 * Stattdessen wird die eine Zeile im Cache gezielt gepatcht. Die bestehende
 * Product.onUpdate-Subscription in useExpansionProductList tut das ebenfalls; der
 * explizite Patch macht den Erfolg deterministisch, falls die Websocket-Verbindung
 * gerade weg ist, und ist gegenüber der Subscription idempotent.
 */
export const useProductPriceRecalculate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productID }: RecalculateVariables) =>
      recalculateProductPrice(productID),
    onSuccess: (result, { productID, expansionID }) => {
      if (!result?.success) {
        return;
      }

      queryClient.setQueryData<Product[]>(
        [QueryKeys.ExpansionProducts, expansionID],
        (prev) =>
          prev?.map((product) =>
            product.id === productID
              ? {
                  ...product,
                  priceStore: result.priceStore ?? product.priceStore,
                  priceOnline: result.priceOnline ?? product.priceOnline,
                  lastStorePriceCalcSuccess: result.storePriceCalcSuccess,
                  lastOnlinePriceCalcSuccess: result.onlinePriceCalcSuccess,
                  lastPriceCalcFailedReason: result.failedReason ?? null,
                }
              : product,
          ),
      );
    },
  });
};
